import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { firstValueFrom } from 'rxjs';

/**
 * De basis-URL voor video's, logo's en andere bestanden uit de opslag —
 * inclusief het kortlevende inlogbewijs dat `/storage/` sindskort eist.
 *
 * Waarom dit een dienst is en niet gewoon `environment.storageUrl`: het
 * bewijs is dynamisch (per sessie gemint) en `<img src>`/`<video src>`
 * kunnen geen Authorization-kop meekrijgen. Het bewijs reist daarom mee ALS
 * onderdeel van de URL zelf: `.../storage/<bewijs>/<pad>`. Bestaande code
 * die `storageUrl + pad` samenvoegt (tientallen plekken in deze app) blijft
 * daardoor ongewijzigd werken — alleen de bron van `storageUrl` verandert
 * van een vaste tekst in deze dienst.
 */
@Injectable({
  providedIn: 'root'
})
export class StorageUrlService {

  private token: string = null;

  constructor(private http: HttpClient) {
  }

  /**
   * De actuele basis-URL, met bewijs. Leeg zolang er nog geen bewijs is
   * opgehaald (vlak na het opstarten van de app, vóór {@link refresh} is
   * afgerond) — een `<img>` met een lege bron toont dan kort niets, en
   * herstelt zichzelf zodra `refresh()` klaar is, omdat elke aanroeper deze
   * getter opnieuw leest.
   */
  public get base(): string {
    return this.token ? `${environment.storageUrl}${this.token}/` : '';
  }

  /**
   * Een nieuw bewijs ophalen. Aanroepen na elke geslaagde inlog, en bij het
   * opstarten van de app als er nog een sessie in de opslag van de browser
   * staat — dat is het enige moment waarop een gebruiker beeld ziet zonder
   * zojuist te hebben ingelogd.
   *
   * Faalt dit (server niet bereikbaar, sessie ongeldig), dan blijft `base`
   * leeg. Dat is geen apart geval om op te vangen: een lege basis levert
   * gewoon gebroken plaatjes op, dezelfde uitkomst als wanneer de gebruiker
   * sowieso niet ingelogd blijkt.
   */
  public async refresh(): Promise<void> {
    try {
      const res = await firstValueFrom(
        this.http.get<{ token: string }>(`${environment.apiUrl}/storage/token`)
      );
      this.token = res?.token ?? null;
    } catch {
      this.token = null;
    }
  }

  /** Bij uitloggen: geen bewijs van de vorige gebruiker laten rondslingeren. */
  public clear(): void {
    this.token = null;
  }
}
